import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom'; 
import './Dashboard.css';
import ListingDetailModal from './ListingDetailModal';
import AddressImpactModal from './AddressImpactModal'; 
import {
 APIProvider,
 Map as GoogleMap,
 Marker,
 InfoWindow,
 useMap,
 useMapsLibrary,
 MapCameraChangedEvent
} from '@vis.gl/react-google-maps';


// --- CONFIGURATION ---
const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';


// --- MAP STYLES ---
const CLEAN_MAP_STYLES = [
 { featureType: "poi", stylers: [{ visibility: "off" }] },
 { featureType: "transit", stylers: [{ visibility: "off" }] },
 { featureType: "road", elementType: "labels", stylers: [{ visibility: "off" }] },
 { featureType: "road", elementType: "geometry", stylers: [{ visibility: "on" }] },
 { featureType: "administrative.locality", elementType: "labels", stylers: [{ visibility: "on" }] }
];


// --- Icons ---
const SearchIcon = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>);
const CloseIcon = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>);
const HeartIcon = () => (<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="heart-icon"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>);
const ChevronDown = () => (<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>);
const NavigationIcon = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" stroke="none" style={{color: '#006aff'}}><polygon points="3 11 22 2 13 21 11 13 3 11"></polygon></svg>);
const LayersIcon = () => (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2"></polygon><polyline points="2 17 12 22 22 17"></polyline><polyline points="2 12 12 17 22 12"></polyline></svg>);
const ChevronLeft = () => (<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>);
const ChevronRight = () => (<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>);
const WarningIcon = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#D32F2F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>);
const TrendUpIcon = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#166534" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline><polyline points="17 6 23 6 23 12"></polyline></svg>);


// --- LOGO COMPONENT ---
const ResitusLogo = () => (
 <div className="zillow-logo">
   <span style={{
     color: '#2a2a33',
     fontWeight: 800,
     fontSize: '24px',
     fontFamily: '"Open Sans", sans-serif',
     letterSpacing: '-0.5px'
   }}>
     Resitus
   </span>
   <span style={{
     color: '#4dabf7',
     fontWeight: 800,
     fontSize: '24px',
     fontFamily: '"Open Sans", sans-serif'
   }}>
     .
   </span>
 </div>
);


// --- Types ---
export interface Listing {
 id: number;
 price: string;
 beds?: number;
 baths?: number;
 sqft?: string;
 address: string;
 lat: number;
 lng: number;
 img: string;
 broker: string;
 isDevelopment?: boolean;
  // Buyer View Data
 badge?: string;
 devStatus?: string;
 devScope?: string;
 pinColor?: 'red' | 'yellow' | 'blue' | 'green';
  // Investor View Data
 investorBadge?: string;
 investorStats?: string;
 investorInsight?: string;
 investorPinColor?: 'green' | 'blue' | 'grey';
 investorCompletion?: string;
 developmentSpecs?: { density: string; parking: string };


 // Details
 meeting?: { date: string; location: string };
 analysis?: { noise: string; traffic: string; character: string };
 timeline?: { date: string; event: string; done: boolean }[];
 tags?: string[];
 schoolImpact?: { level: string; district: string; students: string; note: string };
 shadowImpact?: string;
 constructionPhases?: { name: string; duration: string; color: string; width: string }[];
}


interface PlaceSuggestion {
 place_id: string;
 description: string;
}


interface ViewToggleProps {
 selected: string;
 onToggle: (val: string) => void;
}


// --- Constants ---
const STATUS_OPTIONS = ["Proposed (In Review)", "Approved", "Under Construction", "Denied / Withdrawn"];
const TYPE_OPTIONS = ["Single Family Homes", "Apartments / Multifamily", "Commercial / Retail", "Industrial / Data Center", "Mixed-Use", "Schools / Civic"];
const IMPACT_OPTIONS = ["High Traffic Risk", "Noise / 24hr Ops", "Visual / Privacy Loss", "Tree & Nature Removal", "School Zone Changes", "Density / Crowd Spike"];
const TIMEFRAME_OPTIONS = ["Last 30 Days", "Last 6 Months", "All Active"];


// --- Mock Data ---
const initialListings: Listing[] = [
 {
   id: 1,
   price: "The Pointe at Main",
   address: "📍 10 S. Washington Avenue",
   lat: 40.9275,
   lng: -73.9973,
   img: "/assets/card1.jpg",
   broker: "Famco, LLC and New West Properties, LLC",
   isDevelopment: true,
   badge: "Public Hearing",
   devStatus: "⚠️ Public Hearing Scheduled (Nov 17)",
   devScope: "6-Story Building | 220 Residential Units | Retail",
   pinColor: 'red',
   investorBadge: "📈 MARKET CATALYST",
   investorStats: "220 Market-Rate Units | 6,000 SF Commercial Anchor",
   investorInsight: "Class-A mixed-use supply influx. The addition of 220 residential units will drive foot traffic and retail demand.",
   investorPinColor: 'green',
   investorCompletion: "Est. Completion: Q4 2027",
   developmentSpecs: { density: "Maximized land use (6-Stories on ~2 Acres).", parking: "313 spaces (Structured Garage)" }
 },
 {
   id: 2,
   price: "Muslim Cultural Center Addition",
   address: "📍 52 East Johnson Avenue",
   lat: 40.9283,
   lng: -73.9935,
   img: "/assets/card2.jpg",
   broker: "Muslim Cultural Center (Owner/Applicant)",
   isDevelopment: true,
   badge: "Vote Pending",
   devStatus: "✋ Vote Pending (Nov 03)",
   devScope: "Institutional | Building Addition",
   pinColor: 'yellow',
   investorBadge: "🏛️ CIVIC ANCHOR",
   investorStats: "Institutional Investment | Community Stability",
   investorInsight: "Significant capital investment by long-standing community anchor.",
   investorPinColor: 'blue'
 },
 {
   id: 3,
   price: "Private Garage Addition",
   address: "📍 56 Sylvamdur Avenue",
   lat: 40.9332,
   lng: -73.9985,
   img: "/assets/card3.jpg",
   broker: "Marc Rubin (Homeowner)",
   isDevelopment: true,
   badge: "New Application",
   devStatus: "🆕 New Application (Nov 03)",
   devScope: "Residential | Addition & 1-Car Garage",
   pinColor: 'blue',
   investorBadge: "🔨 EQUITY INVESTMENT",
   investorStats: "Active Permits | ARV Validation",
   investorInsight: "High volume of homeowner-driven capital improvements.",
   investorPinColor: 'grey'
 },
 {
   id: 4,
   price: "Gutwein Home Addition",
   address: "📍 58 Dudley Drive",
   lat: 40.9213,
   lng: -73.9937,
   img: "/assets/card4.jpg",
   broker: "Marina & Andrew Gutwein (Homeowners)",
   isDevelopment: true,
   badge: "Final Approval",
   devStatus: "✅ Resolution Memorialization",
   devScope: "Residential | Addition & Alteration",
   pinColor: 'green',
   investorBadge: "🔨 EQUITY INVESTMENT",
   investorStats: "Active Permits | ARV Validation",
   investorInsight: "Homeowner capital injection.",
   investorPinColor: 'grey'
 },
 {
   id: 5,
   price: "Isser Home Addition",
   address: "📍 38 Frederick Place",
   lat: 40.9345,
   lng: -73.9965,
   img: "/assets/card5.jpg",
   broker: "Jonathan Isser (Homeowner)",
   isDevelopment: true,
   badge: "New Application",
   devStatus: "🆕 New Application (Nov 03)",
   devScope: "Residential | Proposed Addition & Alteration",
   pinColor: 'blue',
   investorBadge: "🔨 EQUITY INVESTMENT",
   investorStats: "Active Permits | ARV Validation",
   investorInsight: "Consistent renovation activity.",
   investorPinColor: 'grey'
 }
];


// --- HELPER COMPONENTS ---


const MapController = ({ center }: { center: [number, number] }) => {
 const map = useMap();
 useEffect(() => {
   if (map) {
     map.panTo({ lat: center[0], lng: center[1] });
     map.setZoom(15);
   }
 }, [map, center]);
 return null;
};


// --- NEW COMPONENT: BoundaryLayer ---
const BoundaryLayer = ({ geoJson }: { geoJson: any }) => {
 const map = useMap();


 useEffect(() => {
   if (!map) return;


   map.data.forEach((feature) => {
     map.data.remove(feature);
   });


   if (geoJson) {
     try {
       map.data.addGeoJson(geoJson);
     } catch (e) {
       console.error("Failed to add GeoJSON to map", e);
     }


     map.data.setStyle({
       strokeColor: '#006AFF',
       strokeWeight: 2,
       strokeOpacity: 1,
       fillColor: '#006AFF',
       fillOpacity: 0 // Transparent interior
     });
    
     const bounds = new google.maps.LatLngBounds();
     map.data.forEach((feature) => {
       const geometry = feature.getGeometry();
       if (geometry) {
         geometry.forEachLatLng((latLng) => {
           bounds.extend(latLng);
         });
       }
     });
     if (!bounds.isEmpty()) {
       map.fitBounds(bounds);
     }
   }
 }, [map, geoJson]);


 return null;
};


const ImpactCircle = ({ center, radius, color }: { center: google.maps.LatLngLiteral, radius: number, color: string }) => {
 const map = useMap();
 const circleRef = useRef<google.maps.Circle | null>(null);


 useEffect(() => {
   if (!map || typeof google === 'undefined') return;


   let hex = '#95a5a6';
   if (color === 'red') hex = '#D32F2F';
   else if (color === 'yellow') hex = '#FBC02D';
   else if (color === 'green') hex = '#388E3C';
   else if (color === 'blue') hex = '#1976D2';


   const circle = new google.maps.Circle({
     strokeColor: hex,
     strokeOpacity: 0.6,
     strokeWeight: 1,
     fillColor: hex,
     fillOpacity: 0.15,
     map,
     center,
     radius,
     zIndex: 0,
     clickable: false
   });


   circleRef.current = circle;


   return () => {
     circle.setMap(null);
   };
 }, [map, center, radius, color]);


 return null;
};


const ZoomControls = () => {
 const map = useMap();
 const handleZoomIn = () => { if (map) map.setZoom((map.getZoom() || 0) + 1); };
 const handleZoomOut = () => { if (map) map.setZoom((map.getZoom() || 0) - 1); };
 return (
   <div className="zoom-controls">
     <button className="zoom-btn" onClick={handleZoomIn}>+</button>
     <div className="zoom-divider" />
     <button className="zoom-btn" onClick={handleZoomOut}>−</button>
   </div>
 );
};


const MapLegend = () => (
 <div className="map-legend" style={{ bottom: '120px' }}>
   <div className="legend-header">Project Status</div>
   <div className="legend-item"><span className="legend-dot red">!</span><span>Hearing Scheduled</span></div>
   <div className="legend-item"><span className="legend-dot yellow" style={{ color: 'white' }}>?</span><span>Vote Pending</span></div>
   <div className="legend-item">
     <span className="legend-dot green" style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
       <svg viewBox="0 0 24 24" width="16" height="16" fill="white" style={{overflow: 'visible'}}>
         <g transform="rotate(45 12 12)">
            <rect x="11" y="4" width="2" height="11" rx="1" />
            <rect x="8" y="4" width="8" height="3" rx="1" />
         </g>
       </svg>
     </span>
     <span>Approved / Active</span>
   </div>
   <div className="legend-item">
     <span className="legend-dot blue" style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
       <svg viewBox="0 0 24 24" width="16" height="16" fill="white" style={{overflow: 'visible'}}>
          <path d="M12 5 L6 10 H8 V15 H16 V10 H18 L12 5 Z" />
       </svg>
     </span>
     <span>Residential Variance</span>
   </div>
 </div>
);


// --- SVG Generators ---
const getPinIcon = (color: string) => {
 if (typeof google === 'undefined') return undefined;
  let hex;
 let content = "";
 const yPos = "9.5";
 const fontSize = "13";
  if (color === 'red') {
   hex = '#D32F2F';
   content = `<text x="12" y="${yPos}" dominant-baseline="central" text-anchor="middle" font-family="Arial" font-weight="bold" font-size="${fontSize}" fill="white">!</text>`;
 } else if (color === 'yellow') {
   hex = '#FBC02D';
   content = `<text x="12" y="${yPos}" dominant-baseline="central" text-anchor="middle" font-family="Arial" font-weight="bold" font-size="${fontSize}" fill="white">?</text>`;
 } else if (color === 'green') {
   hex = '#388E3C';
   content = `
     <g transform="translate(12 9.5) scale(0.7) translate(-12 -9.5) rotate(45 12 9.5)">
       <rect x="11" y="4" width="2" height="11" rx="1" fill="white" />
       <rect x="8" y="4" width="8" height="3" rx="1" fill="white" />
     </g>`;
 } else if (color === 'blue') {
   hex = '#1976D2';
   content = `
     <g transform="translate(12 9.5) scale(0.7) translate(-12 -9.5)">
       <path d="M12 5 L6 10 H8 V15 H16 V10 H18 L12 5 Z" fill="white" />
     </g>`;
 } else {
   hex = color === 'grey' ? '#95a5a6' : '#2ecc71';
   content = ``;
 }


 const svg = `
 <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="40" height="40">
   <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
     <feDropShadow dx="0" dy="1" stdDeviation="1" flood-color="rgba(0,0,0,0.4)"/>
   </filter>
   <path fill="${hex}" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" stroke="white" stroke-width="1.5" style="filter:url(#shadow);" />
   ${content}
 </svg>`;


 return {
   url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
   scaledSize: new google.maps.Size(40, 40),
   anchor: new google.maps.Point(20, 40)
 };
};


const getAppleLocationIcon = () => {
 if (typeof google === 'undefined') return undefined;
 // STATIC BLUE DOT (For Address Search)
 const svg = `
 <svg width="26" height="26" viewBox="0 0 26 26" xmlns="http://www.w3.org/2000/svg">
   <circle cx="13" cy="13" r="12" fill="white" />
   <circle cx="13" cy="13" r="9" fill="#007AFF" />
 </svg>`;
 return {
   url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
   scaledSize: new google.maps.Size(26, 26),
   anchor: new google.maps.Point(13, 13)
 };
};


// NEW: ANIMATED PULSING DOT (For Current Location Only) - LARGER SIZE
const getPulsingLocationIcon = () => {
 if (typeof google === 'undefined') return undefined;
  // INCREASED SIZE to 80x80 to allow larger pulse radius
 const svg = `
 <svg width="80" height="80" viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg">
   <!-- Pulsating Halo -->
   <circle cx="40" cy="40" r="12" fill="#007AFF" opacity="0.4">
     <animate attributeName="r" from="12" to="35" dur="1.5s" repeatCount="indefinite" />
     <animate attributeName="opacity" from="0.4" to="0" dur="1.5s" repeatCount="indefinite" />
   </circle>
   <!-- White Border -->
   <circle cx="40" cy="40" r="12" fill="white" />
   <!-- Blue Center -->
   <circle cx="40" cy="40" r="9" fill="#007AFF" />
 </svg>`;


 return {
   url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
   scaledSize: new google.maps.Size(80, 80),
   anchor: new google.maps.Point(40, 40) // Centered
 };
};


const getPriceIcon = (text: string) => {
 if (typeof google === 'undefined') return undefined;
 const w = text.length * 9 + 20; const h = 30;
 const svg = `
 <svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">
   <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
     <feDropShadow dx="0" dy="1" stdDeviation="1" flood-color="rgba(0,0,0,0.3)"/>
   </filter>
   <rect x="2" y="2" width="${w-4}" height="${h-4}" rx="12" fill="#d93c23" stroke="white" stroke-width="2" style="filter:url(#shadow);" />
   <text x="50%" y="50%" dominant-baseline="central" text-anchor="middle" fill="white" font-family="Arial, sans-serif" font-weight="bold" font-size="13">${text}</text>
 </svg>`;
 return {
   url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
   scaledSize: new google.maps.Size(w, h),
   anchor: new google.maps.Point(w/2, h/2)
 };
};


const ViewToggle = ({ selected, onToggle }: ViewToggleProps) => (
 <div className="view-toggle-container">
   <div className={`toggle-option ${selected === 'Buyer' ? 'active' : ''}`} onClick={() => onToggle('Buyer')}>Buyer</div>
   <div className={`toggle-option ${selected === 'Investor' ? 'active' : ''}`} onClick={() => onToggle('Investor')}>Investor</div>
 </div>
);


// --- SEARCH BAR COMPONENT ---
// UPDATED: Added onClear prop and X button
const SearchBar = ({
 onPlaceSelect,
 setBoundaryGeoJson,
 initialValue,
 onClear // New prop
}: {
 onPlaceSelect: (lat: number, lng: number, address?: string, isCurrent?: boolean) => void,
 setBoundaryGeoJson: (data: any) => void,
 initialValue?: string,
 onClear: () => void // Callback for clearing
}) => {
 const [query, setQuery] = useState(initialValue || ""); // Init with URL value
 const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
 const [showDropdown, setShowDropdown] = useState(false);
 const dropdownRef = useRef<HTMLDivElement>(null);
 const shouldSearchRef = useRef(!initialValue); // Don't search automatically if restored from URL
  const boundaryCache = useRef(new window.Map<string, any>());
  const map = useMap();
 const placesLib = useMapsLibrary('places');
 const geocodingLib = useMapsLibrary('geocoding');
  const [autocompleteService, setAutocompleteService] = useState<google.maps.places.AutocompleteService | null>(null);
 const [geocoder, setGeocoder] = useState<google.maps.Geocoder | null>(null);


 useEffect(() => {
   if (placesLib && !autocompleteService) setAutocompleteService(new placesLib.AutocompleteService());
   if (geocodingLib && !geocoder) setGeocoder(new geocodingLib.Geocoder());
 }, [placesLib, geocodingLib, autocompleteService, geocoder]);


 useEffect(() => {
   const handleClickOutside = (event: MouseEvent) => {
     if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
       setShowDropdown(false);
     }
   };
   document.addEventListener("mousedown", handleClickOutside);
   return () => document.removeEventListener("mousedown", handleClickOutside);
 }, []);


 useEffect(() => {
   const fetch = async () => {
     if (!query || !autocompleteService || !shouldSearchRef.current) {
       if (!query) setSuggestions([]);
       return;
     }
    
     let locationBias;
     if (map) {
       const center = map.getCenter();
       if (center) {
         locationBias = { radius: 5000, center: { lat: center.lat(), lng: center.lng() } };
       }
     }
     const request: google.maps.places.AutocompletionRequest = {
       input: query,
       componentRestrictions: { country: 'us' },
       locationBias: locationBias,
     };
     autocompleteService.getPlacePredictions(request, (results, status) => {
       if (status === google.maps.places.PlacesServiceStatus.OK && results) {
         // FIXED: Filter out ", USA" directly in the dropdown suggestions
         setSuggestions(results.map(place => ({ 
            place_id: place.place_id, 
            description: place.description.replace(', USA', '') 
         })));
         setShowDropdown(true);
       } else { setSuggestions([]); }
     });
   };
   const delayDebounceFn = setTimeout(fetch, 200);
   return () => clearTimeout(delayDebounceFn);
 }, [query, autocompleteService, map]);


 // --- PARALLEL FETCH LOGIC ---
 const fetchTownBoundary = async (searchQuery: string) => {
   if (/^\d/.test(searchQuery)) {
     setBoundaryGeoJson(null);
     return;
   }


   if (boundaryCache.current.has(searchQuery)) {
     setBoundaryGeoJson(boundaryCache.current.get(searchQuery));
     return;
   }


   try {
     const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(searchQuery)}&polygon_geojson=1&format=json`;
     const response = await fetch(url);
     const data = await response.json();


     if (data && data.length > 0) {
       const result = data.find((item: any) => item.geojson && (item.geojson.type === 'Polygon' || item.geojson.type === 'MultiPolygon'));
      
       if (result) {
         const geoData = {
           type: "Feature",
           geometry: result.geojson,
           properties: {}
         };
         boundaryCache.current.set(searchQuery, geoData);
         setBoundaryGeoJson(geoData);
       } else {
         setBoundaryGeoJson(null);
       }
     } else {
       setBoundaryGeoJson(null);
     }
   } catch (err) {
     console.error("Nominatim fetch error:", err);
     setBoundaryGeoJson(null);
   }
 };


 // NEW: Handler for clicking the Search Icon or pressing Enter
 const handleSearchSubmit = () => {
   if (!query || !geocoder) return;
   shouldSearchRef.current = false;
   setShowDropdown(false);


   geocoder.geocode({ address: query }, (results, status) => {
     if (status === 'OK' && results && results[0]) {
       const { lat, lng } = results[0].geometry.location;
       // Format address by removing ", USA"
       const formattedAddress = results[0].formatted_address.replace(', USA', '');
      
       // Update State
       setQuery(formattedAddress);
       fetchTownBoundary(formattedAddress);
       onPlaceSelect(lat(), lng(), formattedAddress, false);
     }
   });
 };


 const handleSelect = (placeId: string, description: string) => {
   shouldSearchRef.current = false;
   // Description is already stripped of ", USA" thanks to useEffect above
   // but we do it again just to be safe if passed raw
   const formattedDesc = description.replace(', USA', '');
   setQuery(formattedDesc);
   setShowDropdown(false);
  
   // Fire fetch immediately
   fetchTownBoundary(description);


   if (geocoder) {
     geocoder.geocode({ placeId }, (results, status) => {
       if (status === 'OK' && results && results[0]) {
         const { lat, lng } = results[0].geometry.location;
         // ADDRESS SEARCH: Pass description, isCurrent = false
         onPlaceSelect(lat(), lng(), formattedDesc, false);
       }
     });
   }
 };


 const handleCurrentLocationClick = () => {
   shouldSearchRef.current = false;
   setShowDropdown(false);
   setQuery("Locating...");


   if (!navigator.geolocation) {
     alert("Geolocation is not supported by your browser");
     return;
   }


   navigator.geolocation.getCurrentPosition(
     (position) => {
       const { latitude: lat, longitude: lng } = position.coords;
      
       // Attempt to Reverse Geocode to get the address string
       if (geocoder) {
         geocoder.geocode({ location: { lat, lng } }, (results, status) => {
           if (status === 'OK' && results && results[0]) {
             // FIXED: Strip ", USA" from current location address too
             const formattedAddress = results[0].formatted_address.replace(', USA', '');
             setQuery(formattedAddress);
             setBoundaryGeoJson(null);
            
             // NOW CALL onPlaceSelect with the found address
             onPlaceSelect(lat, lng, formattedAddress, true);
           } else {
             const coords = `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
             setQuery(coords);
             setBoundaryGeoJson(null);
            
             // Fallback
             onPlaceSelect(lat, lng, "Current Location", true);
           }
         });
       } else {
         // Fallback if no geocoder
         setQuery(`${lat.toFixed(4)}, ${lng.toFixed(4)}`);
         setBoundaryGeoJson(null);
         onPlaceSelect(lat, lng, "Current Location", true);
       }
     },
     (error) => {
       console.error("Error", error);
       setQuery("");
       alert("Unable to retrieve location.");
     }
   );
 };

 // Handle Clear Search
 const handleClearSearch = (e: React.MouseEvent) => {
    e.stopPropagation();
    setQuery("");
    shouldSearchRef.current = false; // Prevent auto-searching on clear
    setSuggestions([]);
    onClear(); // Call parent handler
 };


 return (
   <div className="search-container" ref={dropdownRef}>
     <input
       type="text"
       name="search-address"
       autoComplete="off"
       placeholder="Address, neighborhood, city, ZIP"
       value={query}
       onChange={(e) => {
         setQuery(e.target.value);
         shouldSearchRef.current = true;
         setShowDropdown(true);
       }}
       onFocus={() => {
         if (query) {
            shouldSearchRef.current = true;
            setShowDropdown(true);
         }
       }}
       onKeyDown={(e) => {
         if (e.key === 'Enter') {
           handleSearchSubmit();
         }
       }}
       // Increase right padding if query exists to accommodate X button
       style={{ paddingRight: query ? '70px' : '40px' }} 
     />
     
     {/* SEARCH ICON */}
     <div 
       className="search-icon-wrapper" 
       onClick={handleSearchSubmit} 
       style={{ cursor: 'pointer', pointerEvents: 'auto' }}
     >
       <SearchIcon />
     </div>

     {/* CLEAR (X) BUTTON */}
     {query && (
        <div 
            onClick={handleClearSearch}
            style={{
                position: 'absolute',
                right: '40px', // Left of the search icon
                top: '50%',
                transform: 'translateY(-50%)',
                cursor: 'pointer',
                color: '#94a3b8',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '4px'
            }}
        >
            <CloseIcon />
        </div>
     )}


     {showDropdown && (
       <div className="autocomplete-dropdown">
         <div className="dropdown-item current-location" onClick={handleCurrentLocationClick}>
           <div className="icon-box"><NavigationIcon /></div>
           <div className="item-text"><span className="main-text">Current Location</span></div>
         </div>
         {suggestions.map((place) => (
           <div key={place.place_id} className="dropdown-item" onClick={() => handleSelect(place.place_id, place.description)}>
              <div className="item-text"><span className="main-text">{place.description}</span></div>
           </div>
         ))}
       </div>
     )}
   </div>
 );
};


const getCardPinColor = (item: Listing) => item.pinColor || 'red';


// --- NEW COMPONENT: ListingPopupCarousel ---
const ListingPopupCarousel = ({ listing, onImageClick }: { listing: Listing, onImageClick: () => void }) => {
 const [index, setIndex] = useState(0);
 const [imgError, setImgError] = useState(false);


 // Construct the 3 views: 1. Main Image, 2. Satellite Map, 3. Street View
 const images = useMemo(() => {
   const lat = listing.lat;
   const lng = listing.lng;
   const size = "400x300";
  
   // Live Static Map URL
   const mapUrl = `https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lng}&zoom=19&size=${size}&maptype=satellite&key=${API_KEY}`;
  
   // Live Street View URL
   const svUrl = `https://maps.googleapis.com/maps/api/streetview?size=${size}&location=${lat},${lng}&fov=90&heading=0&pitch=0&key=${API_KEY}`;


   return [
     { url: listing.img, label: 'Rendering' },
     { url: mapUrl, label: 'Satellite' },
     { url: svUrl, label: 'Street View' }
   ];
 }, [listing]);


 // Reset error state when image changes
 useEffect(() => {
   setImgError(false);
 }, [index, listing]);


 const handleNext = (e: React.MouseEvent) => {
   e.stopPropagation();
   setIndex((prev) => (prev + 1) % images.length);
 };


 const handlePrev = (e: React.MouseEvent) => {
   e.stopPropagation();
   setIndex((prev) => (prev - 1 + images.length) % images.length);
 };


 return (
   <div className="popup-image-container" style={{ position: 'relative' }}>
     {/*
        If API fails (imgError), show a gray box.
        Otherwise show the image.
     */}
     {imgError ? (
       <div style={{ width: '100%', height: '100%', background: '#ddd', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666', fontSize: '12px' }}>
         Preview Unavailable
       </div>
     ) : (
       <img
         key={index} // Force re-render on index change
         src={images[index].url}
         alt={images[index].label}
         onClick={onImageClick}
         onError={() => setImgError(true)} // Fallback if API is not enabled
         style={{ cursor: 'pointer', transition: 'opacity 0.2s', width: '100%', height: '100%', objectFit: 'cover' }}
       />
     )}
    
     {/* Label Badge */}
     <div style={{
       position: 'absolute', bottom: '8px', left: '8px',
       background: 'rgba(0,0,0,0.6)', color: 'white',
       fontSize: '10px', padding: '2px 6px', borderRadius: '4px',
       fontWeight: 'bold', pointerEvents: 'none'
     }}>
       {images[index].label}
     </div>


     {/* Navigation Arrows (No Background, White with Shadow) */}
     <button
       onClick={handlePrev}
       style={{
         position: 'absolute', top: '50%', left: '10px', transform: 'translateY(-50%)',
         background: 'none', border: 'none', cursor: 'pointer',
         padding: '0', display: 'flex', alignItems: 'center', color: 'white',
         filter: 'drop-shadow(0 0 3px rgba(0,0,0,0.8))', zIndex: 20
       }}
     >
       <ChevronLeft />
     </button>
    
     <button
       onClick={handleNext}
       style={{
         position: 'absolute', top: '50%', right: '10px', transform: 'translateY(-50%)',
         background: 'none', border: 'none', cursor: 'pointer',
         padding: '0', display: 'flex', alignItems: 'center', color: 'white',
         filter: 'drop-shadow(0 0 3px rgba(0,0,0,0.8))', zIndex: 20
       }}
     >
       <ChevronRight />
     </button>
   </div>
 );
};


// --- MAIN APP ---
function Dashboard() {
 const [searchParams, setSearchParams] = useSearchParams(); // URL PARAMS
 const [hoveredId, setHoveredId] = useState<number | null>(null);
 const disableAutoPan = false;


 // --- INITIALIZE STATE FROM URL ---
 // Parse floats safely to prevent NaN errors
 const latParam = searchParams.get('lat');
 const lngParam = searchParams.get('lng');
 const initialLat = latParam ? parseFloat(latParam) : 40.9275;
 const initialLng = lngParam ? parseFloat(lngParam) : -73.9973;
 
 // Fallback to defaults if NaN
 const validLat = isNaN(initialLat) ? 40.9275 : initialLat;
 const validLng = isNaN(initialLng) ? -73.9973 : initialLng;

 const initialAddress = searchParams.get('address') || null;
 const initialView = searchParams.get('view') || 'Buyer';
 const initialPhase = (searchParams.get('phase') as 'All' | 'Proposed' | 'Active') || 'All';


 const [mapCenter, setMapCenter] = useState<[number, number]>([validLat, validLng]);
 
 // Only set search marker if we have a specific address in URL
 const [searchMarker, setSearchMarker] = useState<{ lat: number, lng: number } | null>(
   initialAddress ? { lat: validLat, lng: validLng } : null
 );
 
 const [isCurrentLocation, setIsCurrentLocation] = useState(false);


 // --- IMPACT BADGE STATE ---
 // Initialize searchedAddress from URL
 const [searchedAddress, setSearchedAddress] = useState<string | null>(initialAddress);
 const [isAddressImpactOpen, setIsAddressImpactOpen] = useState(false);


 const [visibleListings, setVisibleListings] = useState<Listing[]>(initialListings);
 const [viewMode, setViewMode] = useState(initialView); // Init from URL
 const [phaseFilter, setPhaseFilter] = useState<'All' | 'Proposed' | 'Active'>(initialPhase); // Init from URL


 // --- SYNC STATE TO URL ---
 useEffect(() => {
   const newLat = mapCenter[0].toFixed(4);
   const newLng = mapCenter[1].toFixed(4);
   
   // Check if params actually changed before updating URL
   // This prevents infinite loops if setSearchParams triggers re-render
   const currentLat = searchParams.get('lat');
   const currentLng = searchParams.get('lng');
   const currentView = searchParams.get('view');
   const currentAddress = searchParams.get('address');
   const currentPhase = searchParams.get('phase');

   if (
     currentLat !== newLat ||
     currentLng !== newLng ||
     currentView !== viewMode ||
     currentAddress !== (searchedAddress || null) ||
     (currentPhase || 'All') !== phaseFilter
   ) {
      const params: any = {};
      params.lat = newLat;
      params.lng = newLng;
      params.view = viewMode;
      if (phaseFilter !== 'All') params.phase = phaseFilter;
      if (searchedAddress) params.address = searchedAddress;

      setSearchParams(params, { replace: true });
   }
 }, [mapCenter, viewMode, phaseFilter, searchedAddress, searchParams, setSearchParams]);


 const [showFilters, setShowFilters] = useState(false);
 const [showMapOptions, setShowMapOptions] = useState(false);
 const [mapViewOption, setMapViewOption] = useState<'automatic' | 'satellite' | 'street'>('automatic');
 const [showImpactZones, setShowImpactZones] = useState(false);
 const [boundaryGeoJson, setBoundaryGeoJson] = useState<any>(null);


 const [selectedListingId, setSelectedListingId] = useState<number | null>(null);
 const [openDetailId, setOpenDetailId] = useState<number | null>(null);


 const filtersRef = useRef<HTMLDivElement>(null);
 const mapOptionsRef = useRef<HTMLDivElement>(null);


 const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
 const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
 const [selectedImpacts, setSelectedImpacts] = useState<string[]>([]);
 const [selectedTimeframe, setSelectedTimeframe] = useState<string>("");


 const handleCameraChange = (ev: MapCameraChangedEvent) => {
   const bounds = ev.map.getBounds();
   if (!bounds) return;
   const filtered = initialListings.filter(item =>
     bounds.contains({ lat: item.lat, lng: item.lng })
   );
   setVisibleListings(filtered);
 };


 const getMapTypeId = () => {
   switch (mapViewOption) {
     case 'satellite': return 'satellite';
     case 'street': return 'roadmap';
     case 'automatic': default: return 'roadmap';
   }
 };


 useEffect(() => {
   function handleClickOutside(event: MouseEvent) {
     if (filtersRef.current && !filtersRef.current.contains(event.target as Node)) setShowFilters(false);
     if (mapOptionsRef.current && !mapOptionsRef.current.contains(event.target as Node)) setShowMapOptions(false);
   }
   document.addEventListener("mousedown", handleClickOutside);
   return () => document.removeEventListener("mousedown", handleClickOutside);
 }, []);


 // UPDATED: Accepts "address" string and "isCurrent" flag
 const handlePlaceSelect = (lat: number, lng: number, address?: string, isCurrent = false) => {
   setMapCenter([lat, lng]);
   setSearchMarker({ lat, lng });
   setIsCurrentLocation(isCurrent); // Update state to trigger pulsating dot
  
   // Update the Address Badge State
   if (isCurrent) {
      setSearchedAddress(address || "Current Location");
   } else if (address && /\d/.test(address)) {
      setSearchedAddress(address);
   } else {
      setSearchedAddress(null);
   }
 };

 // NEW: Handler to clear search state
 const handleSearchClear = () => {
    setSearchedAddress(null);
    setSearchMarker(null);
    setBoundaryGeoJson(null);
    setIsCurrentLocation(false);
    // URL will automatically update via the useEffect hook
 };


 const toggleStatus = (s: string) => setSelectedStatuses(p => p.includes(s) ? p.filter(i => i !== s) : [...p, s]);
 const toggleType = (t: string) => setSelectedTypes(p => p.includes(t) ? p.filter(i => i !== t) : [...p, t]);
 const toggleImpact = (i: string) => setSelectedImpacts(p => p.includes(i) ? p.filter(x => x !== i) : [...p, i]);
 const clearFilters = () => { setSelectedStatuses([]); setSelectedTypes([]); setSelectedImpacts([]); setSelectedTimeframe(""); };
 const applyFilters = () => setShowFilters(false);
 const totalFilters = selectedStatuses.length + selectedTypes.length + selectedImpacts.length + (selectedTimeframe ? 1 : 0);
  const handleMarkerClick = (id: number) => {
   setSelectedListingId(id);
 };
  const handleOpenDetail = (id: number) => {
   setOpenDetailId(id);
 };
  const closeDetailModal = () => { setOpenDetailId(null); };


 const getCardBadge = (item: Listing) => viewMode === 'Buyer' ? item.badge : item.investorBadge;
 const getCardSub = (item: Listing) => viewMode === 'Buyer' ? item.devStatus : item.investorStats;


 const filteredListings = useMemo(() => {
   return visibleListings.filter(item => {
     if (!item.isDevelopment) return true;
     const color = getCardPinColor(item);
     if (phaseFilter === 'Active' && color !== 'green') return false;
     if (phaseFilter === 'Proposed' && !['red', 'yellow', 'blue'].includes(color)) return false;
     return true;
   });
 }, [visibleListings, phaseFilter]);


 return (
   <APIProvider apiKey={API_KEY} libraries={['places', 'geocoding', 'visualization']}>
     {/*
        --- FORCE HIDE GOOGLE MAPS UI ---
        This style block forces the hidden state of Google's UI elements
        (Shortcuts, Logo, Terms) as requested.
     */}
     <style>
       {`
         .gmnoprint, .gm-style-cc,
         a[href^="http://maps.google.com/maps"],
         a[href^="https://maps.google.com/maps"],
         a[href^="https://www.google.com/maps"] {
           display: none !important;
         }
        
         /* --- FIXED: BOUNDARY BUTTON TOP RIGHT --- */
         .boundary-control-wrapper {
           position: absolute;
           top: 20px !important;
           right: 20px !important;
           left: auto !important; /* Force override of centering */
           transform: none !important; /* Force override of centering */
           z-index: 100;
         }
        
         .remove-boundary-btn {
           background: white;
           border: 2px solid #006aff;
           border-radius: 4px;
           padding: 8px 16px;
           color: #2a2a33;
           font-weight: 700;
           font-size: 14px;
           display: flex;
           align-items: center;
           gap: 12px;
           cursor: pointer;
           box-shadow: 0 4px 12px rgba(0,0,0,0.15);
           transition: all 0.2s;
         }
        
         .remove-boundary-btn:hover {
           background-color: #f0f7ff;
         }
        
         .remove-boundary-btn .close-x {
           color: #555;
           font-size: 20px;
           line-height: 1;
         }

         /* --- NEW: IMPACT BADGE (TOP LEFT) --- */
         .impact-badge-container {
            position: absolute;
            top: 20px;
            left: 20px;
            z-index: 1000;
            background: white;
            border-radius: 8px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.2);
            padding: 4px;
            cursor: pointer;
            transition: transform 0.2s;
            display: flex;
            align-items: stretch;
            overflow: hidden;
            border: 1px solid #fee2e2;
            animation: slideIn 0.3s ease-out;
            min-width: 200px; /* Reduced from 240px */
            padding-right: 16px;
         }

         @keyframes slideIn {
            from { transform: translateX(-20px); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
         }

         .impact-badge-container:hover {
            transform: scale(1.02);
         }

         .impact-stripe {
            width: 6px;
            background-color: #d93c23; /* Red for High Impact Demo */
            border-radius: 4px;
            margin-right: 12px;
         }

         .impact-content {
            padding: 8px 16px 8px 0;
            display: flex;
            flex-direction: column;
         }

         .impact-title {
            font-size: 14px;
            font-weight: 800;
            color: #d93c23;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            display: flex;
            align-items: center;
            gap: 8px;
         }

         .impact-subtitle {
             font-size: 11px;
             color: #555;
             font-weight: 600;
             margin-top: 2px;
         }
       `}
     </style>


     <div className="app-container">
      
       {/* 1. LISTING DETAIL MODAL (Existing) */}
       {openDetailId && (
         <ListingDetailModal
           listing={initialListings.find(l => l.id === openDetailId)}
           onClose={closeDetailModal}
           viewMode={viewMode}
         />
       )}

       {/* 2. ADDRESS IMPACT MODAL (New Connection) */}
       {isAddressImpactOpen && searchedAddress && (
         <AddressImpactModal
            address={searchedAddress}
            impactLevel={viewMode === 'Investor' ? 'High Growth' : 'High Impact'}
            nearbyCount={3}
            onClose={() => setIsAddressImpactOpen(false)}
            viewMode={viewMode}
         />
       )}


       <header className="main-header">
         <div className="header-left"></div>
         <div className="header-center"><ResitusLogo /></div>
         <div className="header-right"><button className="text-button">Sign in</button></div>
       </header>


       <div className="filter-bar">
         <SearchBar
           onPlaceSelect={handlePlaceSelect}
           setBoundaryGeoJson={setBoundaryGeoJson} // Pass setter to SearchBar
           initialValue={searchedAddress || ""} // PRE-FILL SEARCH BAR FROM URL
           onClear={handleSearchClear} // Pass clear handler
         />
        
         <div className="filters-wrapper" ref={filtersRef}>
           <button className={`filter-btn ${showFilters || totalFilters > 0 ? 'active' : ''}`} onClick={() => setShowFilters(!showFilters)}>
             Filters {totalFilters > 0 && `(${totalFilters})`} <ChevronDown />
           </button>
           {showFilters && (
             <div className="filters-dropdown">
               <div className="filter-section"><div className="filter-title">STATUS</div><div className="checkbox-grid">{STATUS_OPTIONS.map(o => <label key={o} className="checkbox-label"><input type="checkbox" checked={selectedStatuses.includes(o)} onChange={() => toggleStatus(o)} />{o}</label>)}</div></div>
               <div className="filter-section"><div className="filter-title">TYPE</div><div className="checkbox-grid">{TYPE_OPTIONS.map(o => <label key={o} className="checkbox-label"><input type="checkbox" checked={selectedTypes.includes(o)} onChange={() => toggleType(o)} />{o}</label>)}</div></div>
               {viewMode === 'Buyer' && (
                 <>
                 <div className="filter-section"><div className="filter-title">IMPACT ALERTS</div><div className="checkbox-grid">{IMPACT_OPTIONS.map(o => <label key={o} className="checkbox-label"><input type="checkbox" checked={selectedImpacts.includes(o)} onChange={() => toggleImpact(o)} />{o}</label>)}</div></div>
                 <div className="filter-section"><div className="filter-title">TIMEFRAME</div><div className="radio-group">{TIMEFRAME_OPTIONS.map(o => <label key={o} className="radio-label"><input type="radio" name="timeframe" checked={selectedTimeframe === o} onChange={() => setSelectedTimeframe(o)} />{o}</label>)}</div></div>
                 </>
               )}
               <div className="filter-footer">
                 <button className="clear-btn" onClick={clearFilters}>Clear All</button>
                 <button className="apply-btn" onClick={applyFilters}>APPLY FILTERS ({totalFilters})</button>
               </div>
             </div>
           )}
         </div>


         <div className="phase-toggle-container">
           <div className={`phase-option ${phaseFilter === 'All' ? 'active' : ''}`} onClick={() => setPhaseFilter('All')}>All</div>
           <div className={`phase-option ${phaseFilter === 'Proposed' ? 'active' : ''}`} onClick={() => setPhaseFilter('Proposed')}>Proposed</div>
           <div className={`phase-option ${phaseFilter === 'Active' ? 'active' : ''}`} onClick={() => setPhaseFilter('Active')}>Active</div>
         </div>
        
         <div style={{ marginLeft: 'auto' }}>
           <ViewToggle selected={viewMode} onToggle={setViewMode} />
         </div>
       </div>


       <div className="content-split">
         <div className="map-section">
           <GoogleMap
             defaultCenter={{ lat: validLat, lng: validLng }} // USE VALIDATED COORDS
             defaultZoom={15}
             mapTypeId={getMapTypeId()}
             styles={CLEAN_MAP_STYLES}
             disableDefaultUI={true}
             onCameraChanged={handleCameraChange}
             style={{ width: '100%', height: '100%' }}
             // FIXED: Clicking map background closes the popup
             onClick={() => setSelectedListingId(null)}
           >
             <MapController center={mapCenter} />
             <MapLegend />
            
             {/* RENDER THE BOUNDARY LAYER */}
             <BoundaryLayer geoJson={boundaryGeoJson} />


             {/* RENDER THE BOUNDARY REMOVE BUTTON */}
             {boundaryGeoJson && (
               <div className="boundary-control-wrapper">
                 <button className="remove-boundary-btn" onClick={() => setBoundaryGeoJson(null)}>
                   Remove Boundary <span className="close-x">✕</span>
                 </button>
               </div>
             )}

             {/* RENDER THE IMPACT BADGE (Top Left - If Address Searched) */}
             {searchedAddress && (
                <div 
                  className="impact-badge-container" 
                  onClick={() => setIsAddressImpactOpen(true)}
                  style={{ borderColor: viewMode === 'Investor' ? '#bbf7d0' : '#fee2e2' }}
                >
                   <div 
                     className="impact-stripe" 
                     style={{ backgroundColor: viewMode === 'Investor' ? '#166534' : '#d93c23' }}
                   ></div>
                   <div className="impact-content">
                      <div 
                        className="impact-title" 
                        style={{ color: viewMode === 'Investor' ? '#166534' : '#d93c23' }}
                      >
                         {viewMode === 'Investor' ? <TrendUpIcon /> : <WarningIcon />}
                         {viewMode === 'Investor' ? 'Strong Growth Signal' : 'High Impact'}
                      </div>
                      <div className="impact-subtitle">
                         {viewMode === 'Investor' ? '+12% Projected Equity' : '3 Active Projects Nearby'}
                      </div>
                   </div>
                </div>
             )}


             {/* Render Impact Zones if enabled */}
             {showImpactZones && filteredListings.map(item => {
               if (!item.isDevelopment) return null;
               let radius = 200;
               const color = getCardPinColor(item);
               if (color === 'red') radius = 400;
               else if (color === 'yellow') radius = 300;
               else if (color === 'green') radius = 250;
               else if (color === 'blue') radius = 75;
               return <ImpactCircle key={`circle-${item.id}`} center={{ lat: item.lat, lng: item.lng }} radius={radius} color={color} />;
             })}


             <div className="map-controls-row">
               <div className="map-options-container" ref={mapOptionsRef}>
                 {showMapOptions && (
                   <div className="map-options-modal">
                     <div className="map-options-section">
                       <div className="map-options-header">MAP STYLE</div>
                       <label className="map-option-row"><input type="radio" name="mapType" checked={mapViewOption === 'automatic'} onChange={() => setMapViewOption('automatic')} /><span>Automatic</span></label>
                       <label className="map-option-row"><input type="radio" name="mapType" checked={mapViewOption === 'satellite'} onChange={() => setMapViewOption('satellite')} /><span>Satellite</span></label>
                       <label className="map-option-row"><input type="radio" name="mapType" checked={mapViewOption === 'street'} onChange={() => setMapViewOption('street')} /><span>Street view</span></label>
                     </div>
                     <div className="map-options-divider"></div>
                     <div className="map-options-section">
                       <div className="map-options-header">LAYERS</div>
                       <label className="map-option-row"><input type="checkbox" checked={showImpactZones} onChange={() => setShowImpactZones(!showImpactZones)} /><span>Show Impact Zones</span></label>
                     </div>
                   </div>
                 )}
                 <button className={`map-options-btn ${showMapOptions ? 'active' : ''}`} onClick={() => setShowMapOptions(!showMapOptions)}><LayersIcon /> Map Options <ChevronDown /></button>
               </div>
               <ZoomControls />
             </div>
            
             {/* RENDER THE SEARCH MARKER (PULSATING DOT FOR CURRENT LOCATION, STATIC FOR ADDRESS) */}
             {searchMarker && (
               <Marker
                 position={searchMarker}
                 icon={isCurrentLocation ? getPulsingLocationIcon() : getAppleLocationIcon()}
                 zIndex={10000} /* Always on top */
               />
             )}


             {filteredListings.map((item) => (
               <Marker
                 key={item.id}
                 position={{ lat: item.lat, lng: item.lng }}
                 onClick={(e) => {
                   e.stop(); // Stop event so map doesn't catch it and close popup immediately
                   handleMarkerClick(item.id);
                 }}
                 onMouseOver={() => setHoveredId(item.id)}
                 onMouseOut={() => setHoveredId(null)}
                 icon={item.isDevelopment ? getPinIcon(getCardPinColor(item)) : getPriceIcon(item.price)}
               />
             ))}


             {selectedListingId && (
               <InfoWindow
                 position={{ lat: initialListings.find(i => i.id === selectedListingId)?.lat || 0, lng: initialListings.find(i => i.id === selectedListingId)?.lng || 0 }}
                 onCloseClick={() => setSelectedListingId(null)}
                 pixelOffset={[0, -45]}
                 disableAutoPan={disableAutoPan}
               >
                 {(() => {
                   const item = initialListings.find(i => i.id === selectedListingId);
                   if (!item) return null;
                   return (
                     <div className="popup-card">
                        <ListingPopupCarousel listing={item} onImageClick={() => handleOpenDetail(item.id)} />
                       
                        <div className="popup-heart"><HeartIcon /></div>
                        {getCardBadge(item) && (
                          <span
                            className={`popup-badge ${viewMode === 'Investor' ? 'investor' : ''}`}
                            style={{ zIndex: 10 }}
                          >
                            {getCardBadge(item)}
                          </span>
                        )}


                        <div className="popup-details" onClick={() => handleOpenDetail(item.id)}>
                           <div className="popup-price">{item.price}</div>
                           <div className="popup-sub" style={{ color: viewMode === 'Buyer' ? '#555' : '#006aff', fontWeight: viewMode === 'Buyer' ? 600 : 700 }}>
                             {getCardSub(item)}
                           </div>
                           <div className="popup-address">{item.address}</div>
                           <div className="popup-broker">{item.broker}</div>
                        </div>
                     </div>
                   );
                 })()}
               </InfoWindow>
             )}
           </GoogleMap>
         </div>


         <div className="listings-section">
           <div className="listings-header">
             <h1>{viewMode === 'Buyer' ? 'Active Development Projects' : 'Real Estate Investment Opportunities'}</h1>
             <div className="listings-subhead"><span>{filteredListings.length} results</span></div>
           </div>
           {filteredListings.length === 0 ? (
             <div className="no-results-message" style={{
               backgroundColor: '#fffbeb', // Light yellow
               border: '1px solid #b45309', // Darkish orange/brown
               borderRadius: '4px',
               padding: '16px',
               textAlign: 'left',
               margin: '20px'
             }}>
               <h3 style={{ margin: '0 0 8px 0', color: '#1e293b', fontSize: '18px', fontWeight: 700 }}>End of matching results</h3>
               <p style={{ margin: 0, color: '#334155', fontSize: '14px' }}>
                 Try zooming out to include more results. Or, change your search criteria.
               </p>
             </div>
           ) : (
             <div className="listings-grid">
               {filteredListings.map((item) => (
                 <div
                   key={item.id}
                   className={`listing-card ${selectedListingId === item.id || hoveredId === item.id ? 'highlight-card' : ''}`}
                   onClick={() => handleOpenDetail(item.id)}
                 >
                   <div className="card-image-container">
                     <img src={item.img} alt="House" />
                     {getCardBadge(item) && <span className={`card-badge ${viewMode === 'Investor' ? 'investor' : ''}`}>{getCardBadge(item)}</span>}
                     <button className="card-heart"><HeartIcon /></button>
                   </div>
                   <div className="card-info">
                     <div className="card-price">{item.price}</div>
                     {item.isDevelopment ? (
                       <div className="card-stats development-stats">
                         <div className="dev-status" style={{ color: viewMode === 'Buyer' ? (item.pinColor === 'yellow' ? '#d4a017' : item.pinColor === 'blue' ? '#006aff' : item.pinColor === 'green' ? '#388E3C' : '#d93c23') : '#006aff' }}>
                            {getCardSub(item)}
                         </div>
                         <div className="dev-scope">
                           {viewMode === 'Buyer' ? item.devScope : item.investorCompletion || item.devScope}
                         </div>
                       </div>
                     ) : (
                       <div className="card-stats">
                         <strong>{item.beds}</strong> bds<span className="divider">|</span><strong>{item.baths}</strong> ba<span className="divider">|</span><strong>{item.sqft}</strong> sqft - House for sale
                       </div>
                     )}
                     <div className="card-address">{item.address}</div>
                     <div className="card-broker">{item.broker}</div>
                   </div>
                 </div>
               ))}
             </div>
           )}
         </div>
       </div>
     </div>
   </APIProvider>
 );
}


export default Dashboard;